import { ref } from 'vue'
import { ApiService } from '../services/ApiService.js'

/**
 * AI bullet-point optimization for a single CV entry (premium feature).
 *
 * Sends the entry's bullets + context to the backend, which resolves the
 * CV's target role/company and any reference documents server-side and
 * returns { original, suggestion } pairs for the review panel.
 */
export function useBulletOptimizer() {
  const isOptimizing = ref(false)
  const optimizeError = ref(null)

  /**
   * @param {Object} options
   * @param {string} options.entryType - 'experience' | 'education' | 'activity'
   * @param {string[]} options.bullets - the bullet strings to optimize
   * @param {Object} options.context - entry fields for prompt grounding (job_title, company, ...)
   * @param {number|null} options.cvId - saved CV id (resolves target role/company)
   * @param {number|null} options.cvEntryId - saved CV entry id (resolves reference documents)
   * @param {number|null} options.masterEntryId - master entry id (reference documents fallback)
   * @returns {Promise<{success: boolean, suggestions?: Array<{original: string, suggestion: string}>, error?: string}>}
   */
  const optimizeEntry = async ({ entryType, bullets, context = {}, cvId = null, cvEntryId = null, masterEntryId = null }) => {
    const nonEmptyBullets = (bullets || []).map(b => (b || '').trim()).filter(b => b !== '')
    if (nonEmptyBullets.length === 0) {
      optimizeError.value = 'Add at least one bullet point before optimizing'
      return { success: false, error: optimizeError.value }
    }

    isOptimizing.value = true
    optimizeError.value = null

    try {
      const data = await ApiService.optimizeBullets({
        entry_type: entryType,
        bullets: nonEmptyBullets,
        context,
        cv_id: cvId,
        cv_entry_id: cvEntryId,
        master_entry_id: masterEntryId
      })

      return { success: true, suggestions: data.suggestions || [] }
    } catch (error) {
      // 402/upgrade-required already opened the global UpgradeModal via ApiService
      console.error('Bullet optimization error:', error)
      optimizeError.value = error.message || 'Failed to optimize bullet points'
      return { success: false, error: optimizeError.value }
    } finally {
      isOptimizing.value = false
    }
  }

  const clearOptimizeError = () => {
    optimizeError.value = null
  }

  return {
    isOptimizing,
    optimizeError,
    optimizeEntry,
    clearOptimizeError
  }
}
